'use client';
import React, { useEffect, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { QuotesManager, DelayedQuote } from '@benzinga/quotes-manager';
import { formatLarge } from '@benzinga/utils';
import { ModuleWrapper } from './ModuleWrapper';
import { ModuleTable } from './ModuleTable';
import { TickerCell, PriceChangeBadge, TextCell } from './TableCells';
import type { ColumnsType } from 'antd/es/table';

const TICKERS = ['SPY', 'QQQ', 'DIA', 'IWM', 'GLD', 'USO', 'BTC/USD', 'ETH/USD'];

interface QuoteData extends DelayedQuote {
  id: string;
}

const columns: ColumnsType<QuoteData> = [
  {
    key: 'symbol',
    title: 'Ticker',
    dataIndex: 'symbol',
    render: (_value, record) => <TickerCell ticker={record.symbol} name={record.name} />,
  },
  {
    key: 'lastTradePrice',
    title: 'Price',
    dataIndex: 'lastTradePrice',
    render: (_value, record) => (
      <TextCell>{record.lastTradePrice ? `$${Number(record.lastTradePrice).toFixed(2)}` : '--'}</TextCell>
    ),
  },
  {
    key: 'change',
    title: 'Change',
    dataIndex: 'change',
    render: (_value, record) => {
      if (record.change === undefined || record.changePercent === undefined) {
        return <span className="text-gray-400">--</span>;
      }
      return <PriceChangeBadge change={record.change} changePercent={record.changePercent} />;
    },
  },
  {
    key: 'volume',
    title: 'Avg. Volume',
    dataIndex: 'volume',
    render: (_value, record) => <TextCell>{record.volume ? formatLarge(record.volume) : '--'}</TextCell>,
  },
];

const PreMarketIndexesModule: React.FC = () => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<QuoteData[]>([]);

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const quotesManager = session.getManager(QuotesManager);

        const response = await quotesManager.getDelayedQuotes(TICKERS);

        if (response?.ok) {
          const quotesData: QuoteData[] = TICKERS.map(ticker => ({
            ...response.ok[ticker],
            id: ticker,
            key: ticker,
          })).filter(quote => quote.symbol);
          setData(quotesData);
        } else {
          console.error('No data in response:', response);
        }
      } catch (error) {
        console.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [session]);

  return (
    <>
    <ModuleWrapper title="Pre-Market Indexes">
      <ModuleTable columns={columns} data={data} loading={loading} emptyText="No market data available" />
    </ModuleWrapper>
  </>
  );
};  

export default PreMarketIndexesModule;
